import { Config } from '@/config';
import type { GoogleUser, GoogleCredentialResponse } from '@/types';

const TOKEN_KEY = 'fitaddon_id_token';
const USER_KEY = 'fitaddon_user';

/**
 * Decode a JWT and return its payload (no verification that's the backend's job).
 */
function decodeJwt<T = Record<string, unknown>>(token: string): T {
  const base64 = token.split('.')[1];
  const binary = atob(base64.replace(/-/g, '+').replace(/_/g, '/'));
  const bytes = Uint8Array.from(binary, (c) => c.charCodeAt(0));
  const json = new TextDecoder('utf-8').decode(bytes);
  return JSON.parse(json) as T;
}

// ─── public API ───

export function getStoredToken(): string | null {
  return sessionStorage.getItem(TOKEN_KEY);
}

export function getStoredUser(): GoogleUser | null {
  const raw = sessionStorage.getItem(USER_KEY);
  return raw ? (JSON.parse(raw) as GoogleUser) : null;
}

export function saveCredentials(idToken: string): GoogleUser {
  sessionStorage.setItem(TOKEN_KEY, idToken);
  const user = decodeJwt<GoogleUser>(idToken);
  sessionStorage.setItem(USER_KEY, JSON.stringify(user));
  return user;
}

export function clearCredentials(): void {
  sessionStorage.removeItem(TOKEN_KEY);
  sessionStorage.removeItem(USER_KEY);
}

export function isLoggedIn(): boolean {
  return !!getStoredToken();
}

/**
 * Load the Google Identity Services SDK if not already loaded,
 * then initialize and render the "Sign in with Google" button.
 */
export function initGoogleSignIn(
  buttonContainer: HTMLElement,
  onSuccess: (user: GoogleUser, idToken: string) => void,
): void {
  const render = () => {
    window.google.accounts.id.initialize({
      client_id: Config.GOOGLE_CLIENT_ID,
      callback: (response: GoogleCredentialResponse) => {
        const user = saveCredentials(response.credential);
        onSuccess(user, response.credential);
      },
      context: 'signin',
      ux_mode: 'popup',
    });

    window.google.accounts.id.renderButton(buttonContainer, {
      type: 'standard',
      shape: 'rectangular',
      theme: 'outline',
      size: 'large',
      text: 'signin_with',
      logo_alignment: 'left',
    });
  };

  if (window.google?.accounts?.id) {
    render();
    return;
  }

  const script = document.createElement('script');
  script.src = 'https://accounts.google.com/gsi/client';
  script.async = true;
  script.defer = true;
  script.onload = render;
  document.head.appendChild(script);
}

export function signOut(): void {
  const user = getStoredUser();
  if (user && window.google?.accounts?.id) {
    window.google.accounts.id.revoke(user.email, () => {});
  }
  clearCredentials();
}

// ─── YouTube OAuth (for registration only) ───

/**
 * Redirect the user to Google OAuth with YouTube scope
 * to obtain both an id_token and a YouTube access_token.
 * After consent, Google redirects back with tokens in the URL hash.
 */
export function startYouTubeAuth(): void {
  const redirectUri = window.location.origin + window.location.pathname;
  const scopes = [
    'openid',
    'profile',
    'email',
    'https://www.googleapis.com/auth/youtube.readonly',
  ].join(' ');

  const authUrl =
    `https://accounts.google.com/o/oauth2/v2/auth?` +
    `client_id=${Config.GOOGLE_CLIENT_ID}&` +
    `redirect_uri=${encodeURIComponent(redirectUri)}&` +
    `response_type=token id_token&` +
    `scope=${encodeURIComponent(scopes)}&` +
    `access_type=online&` +
    `nonce=${Date.now()}`;

  window.location.href = authUrl;
}

/**
 * On page load, check the URL hash for tokens returned by the
 * YouTube OAuth redirect.  Returns both tokens if present, or null.
 */
export function extractYouTubeTokensFromUrl(): {
  idToken: string;
  accessToken: string;
  user: GoogleUser;
} | null {
  const hash = window.location.hash;
  if (!hash) return null;

  const params = new URLSearchParams(hash.substring(1));
  const accessToken = params.get('access_token');
  const idToken = params.get('id_token');

  if (!accessToken || !idToken) return null;

  // Clean the URL
  window.history.replaceState({}, document.title, window.location.pathname);

  const user = saveCredentials(idToken);
  return { idToken, accessToken, user };
}
